import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useEffect } from 'react';

// Module mapping: maps route paths to module IDs for staff permission checking
const ROUTE_MODULE_MAPPING = {
  '/students': 'students',
  '/admission/new': 'students',
  '/teachers': 'teachers',
  '/teachers/new': 'teachers',
  '/staff': 'staff',
  '/classes': 'students',
  '/timetable': 'timetable',
  '/attendance': 'attendance',
  '/exams': 'exams',
  '/online-exam': 'exams',
  '/fee-management': 'fees',
  '/accounts': 'accounts',
  '/hr': 'hr',
  '/staff-qr-attendance': 'hr',
  '/leave-config': 'hr',
  '/salary': 'hr',
  '/reports': 'reports',
  '/analytics': 'reports',
  '/academic-calendar': 'timetable',
  '/promotion': 'students',
  '/store': 'store',
  '/behavior': 'behavior',
  '/announcements': 'notifications',
  '/communication': 'notifications',
  '/fee-reminders': 'fees',
  '/library': 'library',
  '/notices': 'notifications',
  '/homework': 'homework',
  '/certificates': 'certificates',
  '/id-cards': 'students',
  '/id-card-builder': 'students',
  '/alumni': 'students',
  '/hostel': 'hostel',
  '/gate-pass': 'hostel',
  '/transport': 'transport',
};

const ProtectedRoute = ({ children, allowedRoles, requiredModule }) => {
  const { user, token, loading, hasModuleAccess, userPermissions, refreshAuth } = useAuth();
  const location = useLocation();

  // Try to refresh auth if we have token but no user (and not loading)
  useEffect(() => {
    if (token && !user && !loading) {
      // Check if we should retry
      const retryCount = parseInt(sessionStorage.getItem('auth_retry_count') || '0', 10);
      if (retryCount < 1) {
        sessionStorage.setItem('auth_retry_count', String(retryCount + 1));
        refreshAuth?.();
      }
    } else if (user) {
      // Clear retry counter on successful auth
      sessionStorage.removeItem('auth_retry_count');
    }
  }, [token, user, loading, refreshAuth]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If we have token but no user after loading is done, wait briefly for refresh
  if (token && !user) {
    const retryCount = parseInt(sessionStorage.getItem('auth_retry_count') || '0', 10);
    if (retryCount < 1) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="ml-3 text-gray-600">Loading session...</p>
        </div>
      );
    }
    // Max retries exceeded - clear and redirect
    sessionStorage.removeItem('auth_retry_count');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  }

  if (!user) {
    // Check if this is a logout redirect (from sessionStorage)
    const logoutRedirect = sessionStorage.getItem('logout_redirect');
    if (logoutRedirect) {
      sessionStorage.removeItem('logout_redirect');
      return <Navigate to={`/${logoutRedirect}`} replace />;
    }
    
    // Check if user was logged in via tenant (school_code in localStorage)
    const schoolCode = localStorage.getItem('school_code');
    if (schoolCode) {
      return <Navigate to={`/${schoolCode}`} replace />;
    }
    return <Navigate to="/auth" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/auth" replace />;
  }

  // For staff role, check module-level permissions
  if (user.role === 'staff') {
    // Determine required module from prop or route path
    const moduleId = requiredModule || ROUTE_MODULE_MAPPING[location.pathname];
    
    if (moduleId) {
      // Wait for permissions to load
      if (!userPermissions) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        );
      }
      
      // Check if staff has access to the required module
      if (!hasModuleAccess(moduleId)) {
        return <Navigate to="/staff-dashboard" replace />;
      }
    }
  }

  return children;
};

export default ProtectedRoute;
